import { ENV as ENVFromVarlock } from "varlock/env";
import type { TypedEnvSchema } from "varlock/env";

export {
  getRedactionHoldbackLength,
  getRedactionMapInfo,
  initVarlockEnv,
  redactSensitiveConfig,
  resetRedactionMap,
  revealSensitiveConfig,
  scanForLeaks,
  varlockSettings,
} from "varlock/env";
export type { TypedEnvSchema } from "varlock/env";

type ExpoPublicEnvKey = Extract<keyof TypedEnvSchema, `EXPO_PUBLIC_${string}`>;

export type ExpoPublicTypedEnvSchema = Readonly<Pick<TypedEnvSchema, ExpoPublicEnvKey>>;

const publicEnvPrefix = "EXPO_PUBLIC_";

const assertExpoPublicEnvKey = (prop: string) => {
  if (!prop.startsWith(publicEnvPrefix)) {
    throw new Error(
      `ENV.${prop} is not public. Public environment reads must use EXPO_PUBLIC_* keys.`,
    );
  }
};

// The public surface: only `EXPO_PUBLIC_*` keys are readable. These are the
// babel-inlined vars available in every runtime (client bundle, workerd, and
// bare Node), so isomorphic code can safely read them here. To read a sensitive
// (non-public) var from server/CLI code, import `ENV` from the `./env/sensitive`
// entry instead.
export const ENV = new Proxy(ENVFromVarlock as ExpoPublicTypedEnvSchema, {
  get(target, prop, receiver) {
    if (typeof prop === "string") {
      assertExpoPublicEnvKey(prop);
    }

    // `EXPO_PUBLIC_*` reads are babel-inlined at build, so this proxy is only hit
    // by non-inlinable dynamic reads. On Node the varlock graph answers; on
    // workerd (no subprocess to load the graph) the varlock proxy throws, so we
    // fall back to `process.env` (EAS Hosting injects `EXPO_PUBLIC_*` too). See
    // `auto-load.ts` for the full rationale.
    try {
      return Reflect.get(target, prop, receiver);
    } catch {
      return typeof prop === "string" ? process.env[prop] : undefined;
    }
  },

  getOwnPropertyDescriptor(target, prop) {
    if (typeof prop === "string" && !prop.startsWith(publicEnvPrefix)) {
      return undefined;
    }

    return Reflect.getOwnPropertyDescriptor(target, prop);
  },

  has(target, prop) {
    if (typeof prop === "string" && !prop.startsWith(publicEnvPrefix)) {
      return false;
    }

    return Reflect.has(target, prop);
  },

  ownKeys(target) {
    return Reflect.ownKeys(target).filter((prop) => {
      return typeof prop !== "string" || prop.startsWith(publicEnvPrefix);
    });
  },
}) as ExpoPublicTypedEnvSchema;
